import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    keepaliveUrl: String,
    logoutUrl: String,
    reset: { type: Boolean, default: false },
    timeout: { type: Number, default: 5 * 60 * 1000 },
    keepaliveInterval: { type: Number, default: 2 * 60 * 1000 },
  };

  connect() {
    this.activeSinceLastKeepalive = false;
    this.keepalivePending = false;
    this.redirecting = false;

    this.recordActivity = this.recordActivity.bind(this);
    this.checkIdleTimeout = this.checkIdleTimeout.bind(this);
    this.sendKeepaliveIfActive = this.sendKeepaliveIfActive.bind(this);

    this.activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, this.recordActivity, {
        passive: true,
      });
    });

    document.addEventListener("visibilitychange", this.checkIdleTimeout);
    window.addEventListener("focus", this.checkIdleTimeout);
    window.addEventListener("storage", this.checkIdleTimeout);

    this.initializeActivity();

    const keepaliveInterval = Math.min(
      this.keepaliveIntervalValue,
      this.timeoutValue / 2,
    );

    this.keepaliveTimer = setInterval(
      this.sendKeepaliveIfActive,
      keepaliveInterval,
    );

    this.idleTimer = setInterval(this.checkIdleTimeout, 10 * 1000);
  }

  disconnect() {
    this.activityEvents.forEach((eventName) => {
      window.removeEventListener(eventName, this.recordActivity);
    });

    document.removeEventListener("visibilitychange", this.checkIdleTimeout);
    window.removeEventListener("focus", this.checkIdleTimeout);
    window.removeEventListener("storage", this.checkIdleTimeout);

    clearInterval(this.keepaliveTimer);
    clearInterval(this.idleTimer);
  }

  get activityEvents() {
    return ["keydown", "mousedown", "mousemove", "scroll", "touchstart"];
  }

  get lastActivityAt() {
    return Number(localStorage.getItem("lastActivityAt")) || 0;
  }

  initializeActivity() {
    if (this.resetValue || !this.lastActivityAt) {
      localStorage.setItem("lastActivityAt", Date.now().toString());
    }

    this.activeSinceLastKeepalive = false;
    this.checkIdleTimeout();
  }

  recordActivity() {
    if (this.redirecting) return;

    // Don't allow new activity to revive an expired session.
    if (
      this.lastActivityAt &&
      Date.now() - this.lastActivityAt >= this.timeoutValue
    ) {
      this.logout();
      return;
    }

    localStorage.setItem("lastActivityAt", Date.now().toString());
    this.activeSinceLastKeepalive = true;
  }

  async sendKeepaliveIfActive() {
    if (
      this.redirecting ||
      this.keepalivePending ||
      !this.activeSinceLastKeepalive
    ) {
      return;
    }

    if (this.checkIdleTimeout()) return;

    this.activeSinceLastKeepalive = false;
    this.keepalivePending = true;

    try {
      const response = await fetch(this.keepaliveUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
          Accept: "application/json",
        },
        credentials: "same-origin",
        redirect: "manual",
      });

      if (
        response.status === 401 ||
        response.status === 403 ||
        response.type === "opaqueredirect"
      ) {
        this.logout();
      } else if (!response.ok) {
        this.activeSinceLastKeepalive = true;
        console.warn(`Session keepalive returned ${response.status}`);
      }
    } catch (error) {
      this.activeSinceLastKeepalive = true;
      console.warn("Session keepalive failed:", error);
    } finally {
      this.keepalivePending = false;
    }
  }

  checkIdleTimeout() {
    if (this.redirecting) return true;

    const lastActivityAt = this.lastActivityAt;
    if (!lastActivityAt) return false;

    if (Date.now() - lastActivityAt >= this.timeoutValue) {
      this.logout();
      return true;
    }

    return false;
  }

  logout() {
    if (this.redirecting) return;

    this.redirecting = true;
    window.location.assign(this.logoutUrlValue);
  }
}
